import React, { useEffect, useRef, useState } from 'react';
import PanelWindow from './PanelWindow';

// PanelDesktop — the floating-panel surface. Renders one <PanelWindow> per
// open, non-minimized panel (stacked by zOrder), plus an always-visible
// taskbar for switching between every open panel.
// Still not wired into Dashboard.js as of this PR (PR 2/3 of the MDI
// dashboard plan) — sanity-checked in isolation first; a later PR mounts
// this as a sibling to (not nested inside) the app shell's own scrolling
// content region, so dragging a panel never fights that region's own
// overflow-y-auto.
//
// `renderPanelContent(panel, isVisible)` is a render-prop rather than a fixed
// `children` shape, so this component has zero knowledge of PanelHost/the
// module registry (both land in PR 3) — it only knows how to lay out and
// chrome whatever content it's handed per panel.
//
// MOBILE (chromeEnabled=false): only the front-most non-minimized panel is
// rendered, full-bleed, no drag/resize — reproduces today's exact
// single-panel UX using the same underlying panels/zOrder data, so nothing
// is lost switching between a phone and a desktop on the same account (see
// the plan's mobile-gating section).

function useIsDesktopViewport() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

function useBounds(containerRef) {
  const [bounds, setBounds] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => setBounds({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef]);
  return bounds;
}

function PanelDesktop({
  panels,       // { [id]: panel }
  zOrder,       // string[]
  moduleIcons,  // { [moduleKey]: IconComponent } — looked up per panel, supplied by the caller (PR 3 passes the registry's icons)
  renderPanelContent, // (panel) => ReactNode
  onFocus,
  onMove,
  onResize,
  onMinimize,
  onRestore,
  onToggleMaximize,
  onClose,
  onNewWindow,
}) {
  const containerRef = useRef(null);
  const bounds = useBounds(containerRef);
  const isDesktop = useIsDesktopViewport();

  const openPanels = zOrder.map((id) => panels[id]).filter(Boolean);
  const visiblePanels = isDesktop
    ? openPanels.filter((p) => !p.minimized)
    // Mobile: only the front-most non-minimized panel, matching today's
    // single-active-panel UX with the same underlying data.
    : openPanels.filter((p) => !p.minimized).slice(-1);
  const activePanelId = visiblePanels[visiblePanels.length - 1]?.id;
  const visibleIds = new Set(visiblePanels.map((p) => p.id));

  return (
    <div className="absolute inset-0 flex flex-col pointer-events-none">
      <div ref={containerRef} className="relative flex-1 min-h-0 overflow-hidden pointer-events-none">
        {/* EVERY open panel stays mounted; one that is minimized (or, on a phone,
            not in front) is only hidden. Un-mounting it threw away whatever the
            person was in the middle of — a half-written invoice vanished when
            they stepped away to sell water. Polling screens are told they are
            not active (see renderPanelContent's second argument) and pause. */}
        {bounds.width > 0 && openPanels.map((panel) => (
          <div
            key={panel.id}
            className="pointer-events-auto"
            style={visibleIds.has(panel.id) ? undefined : { display: 'none' }}
            aria-hidden={!visibleIds.has(panel.id)}
          >
            <PanelWindow
              panel={panel}
              bounds={bounds}
              zIndex={zOrder.indexOf(panel.id)}
              isFront={activePanelId === panel.id}
              chromeEnabled={isDesktop}
              Icon={moduleIcons?.[panel.moduleKey]}
              onFocus={onFocus}
              onMove={onMove}
              onResize={onResize}
              onMinimize={onMinimize}
              onToggleMaximize={onToggleMaximize}
              onClose={onClose}
              onNewWindow={isDesktop ? onNewWindow : undefined}
            >
              {renderPanelContent(panel, visibleIds.has(panel.id))}
            </PanelWindow>
          </div>
        ))}
      </div>

      {isDesktop && openPanels.length > 0 && (
        <div className="pointer-events-auto relative z-[100000] flex h-11 flex-shrink-0 items-center gap-1 overflow-x-auto border-t border-gray-700 bg-gray-900 px-2">
          {openPanels.map((panel) => {
            const Icon = moduleIcons?.[panel.moduleKey];
            return (
              <button
                key={panel.id}
                type="button"
                onClick={() => {
                  if (panel.minimized) onRestore(panel.id);
                  onFocus(panel.id);
                }}
                aria-pressed={activePanelId === panel.id}
                title={panel.title}
                className={`flex h-8 max-w-56 flex-shrink-0 items-center gap-1.5 rounded px-3 text-xs font-medium ${
                  activePanelId === panel.id
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" aria-hidden="true" />}
                <span className="truncate">{panel.title}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PanelDesktop;
